import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";
import { FormField } from "@/pages/ContactPage";
import { PlusCircle, Calculator } from "lucide-react";

export default function AdminInvoices() {
  const [invoices, setInvoices] = useState([]);
  const [clients, setClients] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ client_id: "", amount: "", description: "", due_date: "" });
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = () =>
    Promise.all([api.get("/invoices"), api.get("/admin/clients")]).then(([i, c]) => {
      setInvoices(i.data);
      setClients(c.data);
      setLoading(false);
    });

  useEffect(() => { load(); }, []);

  const [syncingId, setSyncingId] = useState(null);
  const syncQbo = async (id) => {
    setSyncingId(id);
    try {
      const { data } = await api.post(`/integrations/qbo/sync-invoice/${id}`);
      toast.success(`Pushed to QuickBooks (${data.qbo_doc_number || data.qbo_invoice_id}).`);
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSyncingId(null);
    }
  };

  const create = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/invoices", { ...form, amount: Number(form.amount) });
      toast.success("Invoice created.");
      setForm({ client_id: "", amount: "", description: "", due_date: "" });
      setShowForm(false);
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.28em] text-slate-500">Invoices</div>
          <h1 className="font-display text-3xl md:text-4xl tracking-tighter font-medium mt-2">Invoices &amp; billing</h1>
        </div>
        <button
          data-testid="new-invoice-btn"
          onClick={() => setShowForm((v) => !v)}
          className="inline-flex items-center gap-2 px-4 py-2 bg-[#1E40AF] hover:bg-[#1E3A8A] text-white text-sm font-semibold rounded-sm"
        >
          <PlusCircle className="h-4 w-4" /> New invoice
        </button>
      </div>

      {showForm && (
        <form onSubmit={create} data-testid="new-invoice-form" className="border border-slate-200 bg-white p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          <label className="block">
            <span className="block text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold mb-1.5">
              Client <span className="text-[#1E40AF] ml-1">*</span>
            </span>
            <select
              data-testid="invoice-client"
              required
              value={form.client_id}
              onChange={(e) => setForm({ ...form, client_id: e.target.value })}
              className="w-full px-3 py-2 bg-white border border-slate-300 outline-none text-sm rounded-sm"
            >
              <option value="">Select a client…</option>
              {clients.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.company || c.name} ({c.email})
                </option>
              ))}
            </select>
          </label>
          <FormField label="Amount (USD)" type="number" step="0.01" required id="amt" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} testId="invoice-amount" />
          <div className="md:col-span-2">
            <FormField label="Description" required id="desc" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} testId="invoice-description" />
          </div>
          <FormField label="Due date" type="date" id="due" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} testId="invoice-due" />
          <div className="md:col-span-2 flex items-center justify-end gap-3">
            <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 text-sm text-slate-600">Cancel</button>
            <button data-testid="create-invoice-btn" disabled={saving} className="px-5 py-2 bg-[#1E40AF] hover:bg-[#1E3A8A] text-white text-sm font-semibold rounded-sm disabled:opacity-60">
              {saving ? "Creating…" : "Create invoice"}
            </button>
          </div>
        </form>
      )}

      <div className="bg-white border border-slate-200">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-[10px] uppercase tracking-[0.2em] text-slate-500">
            <tr>
              <th className="text-left px-5 py-3 font-semibold">Invoice #</th>
              <th className="text-left px-5 py-3 font-semibold">Client</th>
              <th className="text-left px-5 py-3 font-semibold">Description</th>
              <th className="text-right px-5 py-3 font-semibold">Amount</th>
              <th className="text-left px-5 py-3 font-semibold">Status</th>
              <th className="text-right px-5 py-3 font-semibold">Created</th>
              <th className="text-right px-5 py-3 font-semibold">QuickBooks</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan="7" className="px-5 py-10 text-center text-slate-400">Loading…</td></tr>}
            {!loading && invoices.length === 0 && (
              <tr><td colSpan="7" className="px-5 py-10 text-center text-slate-400">No invoices yet.</td></tr>
            )}
            {invoices.map((inv) => (
              <tr key={inv.id} className="border-t border-slate-100 hover:bg-slate-50">
                <td className="px-5 py-3 font-mono-alt text-xs">{inv.invoice_number}</td>
                <td className="px-5 py-3">
                  <div className="font-medium">{inv.client_company || "—"}</div>
                  <div className="text-xs text-slate-500">{inv.client_email}</div>
                </td>
                <td className="px-5 py-3 text-slate-700">{inv.description}</td>
                <td className="px-5 py-3 text-right font-medium tabular-nums">${Number(inv.amount).toLocaleString()}</td>
                <td className="px-5 py-3"><StatusBadge status={inv.status} /></td>
                <td className="px-5 py-3 text-right text-xs text-slate-500 tabular-nums">
                  {new Date(inv.created_at).toLocaleDateString()}
                </td>
                <td className="px-5 py-3 text-right">
                  {inv.qbo_invoice_id ? (
                    <div className="text-[10px] font-mono-alt text-slate-600" title={inv.qbo_invoice_id}>
                      {inv.qbo_doc_number || inv.qbo_invoice_id.slice(-8)}
                    </div>
                  ) : (
                    <button
                      data-testid={`sync-qbo-${inv.id}`}
                      onClick={() => syncQbo(inv.id)}
                      disabled={syncingId === inv.id}
                      className="inline-flex items-center gap-1 px-2 py-1 border border-slate-300 text-[10px] font-semibold text-slate-700 hover:bg-slate-100 rounded-sm disabled:opacity-60"
                    >
                      <Calculator className="h-3 w-3" />
                      {syncingId === inv.id ? "Syncing…" : "Push"}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
